import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { SharedService } from '@services/shared.service';
import { FormsModule } from '@angular/forms';
import { PagesService } from '@services/pages.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { LUQTA_WEBHOOK_URL } from 'src/app/constants/micro-api.service';
import { isArabicLang, ensureArabicFont, drawBilingualText } from '../../../utils/arabic-pdf.util';

@Component({
  selector: 'app-developer',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: './developer.component.html',
  styles: [
    `.promax {
        font-family: Segoe UI, Arial, sans-serif;
        color: #111;
        margin: 0;
        padding: 24px;
        display: flex;
        justify-content: center;
      }
      .wrap {
        width: 100%;
      }
      h1 {
        font-size: 22px;
        margin: 0 0 10px;
        color: #0b5cff;
      }
      h2 {
        font-size: 18px;
        margin: 0 0 8px;
        color: #0b5cff;
      }
      p {
        margin: 6px 0 14px;
        color: #444;
      }
      .card {
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 16px;
        margin-top: 16px;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
      }
      code {
        background: #f3f4f6;
        padding: 3px 6px;
        border-radius: 6px;
        font-family: monospace;
        font-size: 13px;
      }
      pre {
        background: #f3f4f6;
        padding: 10px;
        border-radius: 8px;
        overflow: auto;
        font-size: 13px;
      }
      ul {
        margin: 8px 0 0 18px;
        color: #444;
        line-height: 1.6;
        font-size: 14px;
      }
      // input {
      //   height: 56px;
      //   border-radius: 12px;
      //   background: #f5f5f9;
      //   padding: 21px 18px;
      //   font-size: 14px;
      //   font-weight: 400;
      //   line-height: 16px;
      //   text-align: left;
      //   border: none;
      //   color: #2e3b55;

      //   &:focus,
      //   &:active {
      //     background: #f5f5f9;
      //   }
      // }
      // label {
      //   text-align: left;
      //   color: #2e3b55;
      //   font-size: 18px;
      //   font-weight: 600;
      //   line-height: 21.48px;
      //   margin-bottom: 8px;
      // }
      h1 {
        font-family: 'Salsa' !important;
        font-size: 32px;
        font-weight: 400;
        line-height: 39.23px;
        text-align: left;
        color: #2e3b55;
      }
      h6 {
        padding-top: 18px;
        font-size: 22.46px;
        font-weight: 500;
        line-height: 33.69px;
        text-align: left;
        color: #828999;
      }

      .developer-card {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px;
      }

      .client-libraries-card .bottom {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        align-items: stretch;
      }

      .client-libraries-card .bottom .btn {
        width: 100%;
        min-height: 44px;
        white-space: normal;
        line-height: 1.25;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
      }

      @media (max-width: 992px) {
        /* minmax(0, 1fr) — NOT plain 1fr — so the column can shrink below the
           min-content width of the long API key string / buttons instead of
           blowing the grid wider than the viewport. */
        .developer-card {
          grid-template-columns: minmax(0, 1fr);
        }

        /* Grid/flex items default to min-width:auto and refuse to shrink below
           their content — force them to honour the track width. */
        .developer-card .inner-card,
        .client-libraries-card,
        .client-libraries-card .bottom {
          min-width: 0;
        }
      }

      @media (max-width: 576px) {
        .client-libraries-card .bottom {
          grid-template-columns: 1fr;
        }

        /* Keep "Download API Collection" on a single line in the full-width
           button without overflowing. .btn-sm forces 18px / 24px padding with
           !important, so the smaller size must also be !important to win. */
        .client-libraries-card .bottom .btn {
          white-space: nowrap;
          font-size: 13px !important;
          padding: 7px 10px !important;
          height: auto !important;
          min-height: 42px;
        }
      }
    `,
  ],
})
export class DeveloperComponent {
  isApiKey: boolean = false;
  apiKey: string = '';
  copied: boolean = false;
  clientName!: string;
  isDisabled = true;
  readonly luqtaWebhookUrl = LUQTA_WEBHOOK_URL;
  readonly webhookExampleBody = JSON.stringify({ contest_uid: 'CONTEST_UID', level_uid: 'LEVEL_UID', phone_number: '+9665XXXXXXXX', type: 'app' });
  sharedService = inject(SharedService);
  translateService = inject(TranslateService);
  pagesService = inject(PagesService);
  secureStorage = inject(SecureStorageService);
  constructor(private clipboard: Clipboard) { }
  ngOnInit(): void {
    this.getApiKey();
    this.setClientName();
  }
  setClientName(): void {
    const user = this.secureStorage.getItem<any>('userDetails');
    if (user) {
      this.clientName = user?.name || ''; // e.g. "Adnan Malik"
    }
  }
  generateApiKey(name: string): void {
    this.sharedService.showLoader();
    this.pagesService
      .update('GET_DEVELOPER_API_KEY', { client_name: name })
      .subscribe({
        next: (res: any) => {
          // The gateway hands back the same list the page loads with; Xano returned the bare row.
          const row = Array.isArray(res) ? res[0] : res;
          this.sharedService.hideLoader();
          if (!row?.api_key) {
            this.sharedService.createNotification(
              'error',
              this.translateService.instant('developer.apiKeyGenerationFailed')
            );
            return;
          }
          this.apiKey = row.api_key;
          this.isApiKey = true;
          this.copied = false;
          this.sharedService.createNotification(
            'success',
            this.translateService.instant('developer.apiKeyGenerated')
          );
        },
        error: (err: any) => {
          this.sharedService.createNotification(
            'error',
            err?.error?.message || this.translateService.instant('developer.apiKeyGenerationFailed')
          );
          this.sharedService.hideLoader();
        },
      });
  }
  copyApiKey(apiKeyInput: HTMLInputElement): void {
    if (!this.apiKey) {
      this.sharedService.createNotification(
        'warning',
        this.translateService.instant('developer.noApiKeyToCopy')
      );
      return;
    }
    this.clipboard.copy(this.apiKey);
    apiKeyInput.select();
    this.copied = true;
    this.sharedService.createNotification(
      'success',
      this.translateService.instant('developer.apiKeyCopied')
    );
    setTimeout(() => {
      this.copied = false;
    }, 2000);
  }
  // ✅ Fixed PDF Download
  async downloadDummyPdf(): Promise<void> {
    this.sharedService.showLoader();
    try {
      const { jsPDF } = await import('jspdf');
      const autoTable: any = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const isAr = isArabicLang();
      const arabicFontLoaded = await ensureArabicFont(doc);
      const labels = {
        title: isAr ? 'وثائق واجهة برمجة تطبيقات LuQta' : 'Luqta API Documentation',
        intro: isAr
          ? 'يصف هذا المستند كيفية الوصول إلى نقطة نهاية الحملات. استخدم مفتاح API الذي تم إنشاؤه في صفحة المطور.'
          : 'This document describes how to access the campaigns endpoint. ' +
            'Use the API key generated in the Developer page.',
        endpoint: isAr ? 'نقطة النهاية:' : 'Endpoint:',
        exampleCurl: isAr ? 'مثال (cURL):' : 'Example (cURL):',
        notesHeading: isAr ? 'ملاحظات:' : 'Notes:',
        notes: isAr
          ? [
              '• استبدل YOUR_API_KEY بالمفتاح الذي تم إنشاؤه في هذه الصفحة.',
              '• حافظ على سرية مفتاح API الخاص بك. لا تعرضه علنًا في كود جانب العميل.',
            ]
          : [
              '• Replace YOUR_API_KEY with the key generated on this page.',
              '• Keep your API key secret. Do not expose it in client-side code publicly.',
            ],
      };
      doc.setProperties({ title: labels.title });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      drawBilingualText(doc, labels.title, 40, 50, arabicFontLoaded);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      const introLines = doc.splitTextToSize(labels.intro, 520);
      introLines.forEach((line: string, idx: number) => {
        drawBilingualText(doc, line, 40, 80 + idx * 14, arabicFontLoaded);
      });
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, labels.endpoint, 40, 120, arabicFontLoaded);
      doc.setFont('helvetica', 'normal');
      doc.text(`POST ${LUQTA_WEBHOOK_URL}`, 110, 120);
      // ---- Body fields (Table) ----
      const startY = 150;
      autoTable(doc, {
        startY,
        head: [['Field', 'Type', 'Required', 'Description']],
        body: [
          ['Authorization header', 'string', 'Yes', 'Bearer YOUR_API_KEY (client-specific)'],
          ['contest_uid', 'uuid', 'Yes', 'The campaign'],
          ['level_uid', 'uuid', 'Yes', 'The platform-webhook level'],
          ['phone_number', 'string', 'Yes', 'The player who completed the task'],
          ['type', 'string', 'No', 'Always "app"'],
        ],
        styles: { font: 'helvetica', fontSize: 11 },
        headStyles: { fontStyle: 'bold' },
        margin: { left: 40, right: 40 },
        tableWidth: 520,
      });
      let y = (doc as any).lastAutoTable?.finalY ?? startY + 20;
      y += 30;
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, labels.exampleCurl, 40, y, arabicFontLoaded);
      y += 16;
      doc.setFont('courier', 'normal');
      const curl = `curl -X POST "${LUQTA_WEBHOOK_URL}" \
  -H "Authorization: Bearer YOUR_API_KEY" -H "Content-Type: application/json" \
  -d '{"contest_uid":"CONTEST_UID","level_uid":"LEVEL_UID","phone_number":"+9665XXXXXXXX","type":"app"}'`;
      doc.text(doc.splitTextToSize(curl, 520), 40, y);
      y += 40;
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, labels.notesHeading, 40, y, arabicFontLoaded);
      y += 16;
      doc.setFont('helvetica', 'normal');
      labels.notes.forEach((line: string, idx: number) => {
        drawBilingualText(doc, line, 40, y + idx * 14, arabicFontLoaded);
      });
      // ---- Open in new tab (show), fallback to download if blocked ----
      const blobUrl = doc.output('bloburl');
      const win = window.open(blobUrl, '_blank');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        doc.save('Luqta_API_Documentation.pdf');
      }
      this.sharedService.createNotification(
        'success',
        this.translateService.instant('developer.pdfDownloadStarted')
      );
    } catch (e) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('developer.pdfGenerationFailed') ||
        'PDF generation failed'
      );
    } finally {
      this.sharedService.hideLoader();
    }
  }
  // ✅ Postman Collection Download
  downloadCollection(): void {
    this.sharedService.showLoader();
    const collectionData = {
      info: {
        name: 'Luqta API Collection',
        schema:
          'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
      },
      item: [
        {
          name: 'Complete Webhook Level',
          request: {
            method: 'POST',
            header: [
              {
                key: 'Authorization',
                value: 'Bearer {{apiKey}}',
                type: 'text',
              },
              {
                key: 'Content-Type',
                value: 'application/json',
                type: 'text',
              },
            ],
            body: {
              mode: 'raw',
              raw: JSON.stringify(
                { contest_uid: '{{contestUid}}', level_uid: '{{levelUid}}', phone_number: '{{phoneNumber}}', type: 'app' },
                null,
                2,
              ),
            },
            url: { raw: LUQTA_WEBHOOK_URL },
          },
          response: [],
        },
      ],
      variable: [{ key: 'apiKey', value: 'your api key' }],
    };
    const blob = new Blob([JSON.stringify(collectionData, null, 2)], {
      type: 'application/json',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Luqta_API_Collection.json';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    this.sharedService.hideLoader();
    this.sharedService.createNotification(
      'success',
      this.translateService.instant('developer.collectionDownloadStarted')
    );
  }
  getApiKey(): void {
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_DEVELOPER_API_KEY').subscribe({
      next: (res: any) => {
        if (res?.code === 'ERROR_FATAL' || res?.code?.startsWith?.('ERROR')) {
          this.sharedService.createNotification('info', res.message || 'No API key found. Please generate one.');
        } else if (res?.length > 0 && res[0]) {
          this.isApiKey = !!res[0].api_key;
          this.apiKey = res[0].api_key || '';
          this.clientName = res[0].client_name || this.clientName;
        }
        this.sharedService.hideLoader();
      },
      error: (err: any) => {
        const message = err?.error?.message || err?.message || 'Failed to fetch API key.';
        this.sharedService.createNotification('error', message);
        this.sharedService.hideLoader();
      },
    });
  }
}
